import React, { useState, useEffect, useRef, useMemo } from "react";
import { Box, Button, Container, Grid, Input, Link, SpaceBetween, StatusIndicator } from "@amzn/awsui-components-react";
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { AgGridReact } from 'ag-grid-react';
import { useSelector } from "react-redux";
import { listPreference, deletePreference } from "src/utilities/AppSyncPlusAPIs";
import { getFirstPassHistoryItem } from "src/Ateam-BI/Templates/Utils/AppActivityUtilsAppsyncPlus";
import { convertToLocalTime } from "src/Ateam-BI/CorpReporting/Utility";
import FirstpassConsolidatedExceptionsResultComponent from "./FirstpassConsolidatedExceptionsResultComponent";
import aiInsights from '../../../images/aiInsights.svg'
import { loadResultsFromS3, getSummaryFindingsTable, formatHeader } from "../Utility";
import { downloadRichPdf } from "../pdfUtils";
import { downloadConsolidatedHistoryExcel } from "../excelUtils";
import DownloadFormatModal from "./DownloadFormatModal";
import { EXCEPTION_STATUSES } from "../Constants";

const FIRSTPASS_CONSOLIDATED_PREFERENCE_TYPE = 'FIRSTPASS_CONSOLIDATED_RESULT';
const FIRSTPASS_CONSOLIDATED_ASSOCIATION_TYPE = 'FIRSTPASS_CONSOLIDATED_ALIAS';

const FirstPassConsolidatedHistoryComponent = () => {
  const gridRef = useRef();
  const [savedConsolidatedResults, setSavedConsolidatedResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [quickFilterText, setQuickFilterText] = useState(null);
  const [viewingConsolidatedResult, setViewingConsolidatedResult] = useState(null);
  const [isLoadingResults, setIsLoadingResults] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState({});
  const [downloadLoading, setDownloadLoading] = useState({});
  const [showFormatModal, setShowFormatModal] = useState(false);
  const [pendingDownloadData, setPendingDownloadData] = useState(null);
  const userId = useSelector((globalState) => globalState.auth.userId);

  const FIRSTPASS_CONSOLIDATED_ASSOCIATION_ID = `FIRSTPASS_CONSOLIDATED#${userId}`;

  const defaultColDef = {
    filter: true,
    filterParams: {
      buttons: ['reset'],
      debounceMs: 200
    }
  };

  const columnDefs = useMemo(() => [
    { field: 'label', headerName: 'Report Name', width: 450, sortable: true, resizable: true },
    { field: 'entityCount', headerName: 'Entities', width: 150, sortable: true, resizable: true },
    { field: 'createdTimestamp', headerName: 'Saved On', width: 250, sortable: true, resizable: true, 
      cellRenderer: (params) => params.value || '',
      valueGetter: (params) => params?.data?.createdTimestamp ? convertToLocalTime(parseInt(params.data.createdTimestamp), true) : '' 
    },
    { field: 'createdBy', headerName: 'Saved By', width: 150, sortable: true, resizable: true },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 250,
      sortable: false,
      resizable: true,
      cellRenderer: (params) => {
        const isDeleting = deleteLoading?.[params?.data?.preferenceId];
        const isDownloading = downloadLoading?.[params?.data?.preferenceId];
        return (
          <SpaceBetween direction="horizontal" size="s">
            <Button onClick={() => viewConsolidatedResult(params?.data)}>View</Button>
            <Button
              iconName="download"
              variant="icon"
              onClick={() => handleDownload(params?.data)}
              disabled={isDownloading}
              loading={isDownloading}
            />
            <Button 
              iconName="remove" 
              variant="icon" 
              onClick={() => handleDelete(params?.data?.preferenceId)}
              disabled={isDeleting}
              loading={isDeleting}
            />
          </SpaceBetween>
        );
      }
    },
  ], [deleteLoading, downloadLoading]);

  useEffect(() => {
    loadSavedConsolidatedResults();
  }, [userId]);

  useEffect(() => {
    onFilterTextBoxChanged();
  }, [quickFilterText]);

  function onFilterTextBoxChanged() {
    gridRef?.current?.api?.setGridOption(
      "quickFilterText",
      quickFilterText,
    );
  }

  const loadSavedConsolidatedResults = () => {
    if (!userId) return;
    setIsLoading(true);

    listPreference(
      FIRSTPASS_CONSOLIDATED_ASSOCIATION_ID,
      FIRSTPASS_CONSOLIDATED_ASSOCIATION_TYPE,
      FIRSTPASS_CONSOLIDATED_PREFERENCE_TYPE,
      function onSuccess(response) {
        const items = response?.data?.listPreference?.items || [];
        const parsedItems = items.map(item => {
          let info = {};
          try {
            info = JSON.parse(item.information);
          } catch (e) {
            console.error('Error parsing consolidated result info:', e);
          }
          return {
            ...item,
            entityCount: info?.requestIds?.length || 0,
            requestIds: info?.requestIds || [],
            entityDetails: info?.entityDetails || [],
          };
        }).sort((a, b) => parseInt(b.createdTimestamp) - parseInt(a.createdTimestamp));
        
        setSavedConsolidatedResults(parsedItems);
        setIsLoading(false);
      },
      function onFailure(error) {
        console.error('Error loading saved consolidated results:', error);
        setIsLoading(false);
      }
    );
  };

  const handleDelete = (preferenceId) => {
    setDeleteLoading(prev => ({ ...prev, [preferenceId]: true }));
    deletePreference(
      preferenceId,
      null,
      () => {
        setSavedConsolidatedResults(prev => prev.filter(item => item.preferenceId !== preferenceId));
        setDeleteLoading(prev => ({ ...prev, [preferenceId]: false }));
        // If we're viewing this result, clear it
        if (viewingConsolidatedResult?.preferenceId === preferenceId) {
          setViewingConsolidatedResult(null);
        }
      },
      (error) => {
        console.error('Error deleting consolidated result:', error);
        setDeleteLoading(prev => ({ ...prev, [preferenceId]: false }));
      }
    );
  };

  const handleDownload = (savedResult) => {
    setPendingDownloadData(savedResult);
    setShowFormatModal(true);
  };

  const handleDownloadConfirm = async (format) => {
    setShowFormatModal(false);
    const savedResult = pendingDownloadData;
    if (!savedResult) return;

    const preferenceId = savedResult.preferenceId;
    setDownloadLoading(prev => ({ ...prev, [preferenceId]: true }));

    try {
      const requestIds = savedResult.requestIds || [];
      const entityDetails = savedResult.entityDetails || [];

      // Fetch history items for each request_id
      const resultsPerRequestId = {};
      const fetchPromises = requestIds.map(requestId => {
        return new Promise((resolve) => {
          getFirstPassHistoryItem(requestId,
            function onSuccess(response) {
              const historyItem = response?.data?.getFirstPassHistoryItem;
              if (historyItem) {
                resultsPerRequestId[requestId] = historyItem;
              }
              resolve();
            },
            function onFailure(error) {
              resolve();
            }
          );
        });
      });

      await Promise.all(fetchPromises);

      if (format === "excel") {
        // Excel download - downloads all data with per-entity sheets
        await downloadConsolidatedHistoryExcel(
          entityDetails,
          resultsPerRequestId,
          savedResult.label || 'Consolidated_Report',
          () => setDownloadLoading(prev => ({ ...prev, [preferenceId]: false }))
        );
      } else {
        // PDF download - existing behavior
        const s3LoadPromises = entityDetails.map(async (detail) => {
          const historyItem = resultsPerRequestId[detail.request_id];
          if (!historyItem || !historyItem.response_s3_path) return [];

          try {
            const s3Data = await loadResultsFromS3(historyItem);
            const summaryFindings = getSummaryFindingsTable(s3Data);

            if (summaryFindings && summaryFindings.length > 0) {
              const exceptionFindings = summaryFindings.filter(
                finding => EXCEPTION_STATUSES.includes(finding.severity)
              );

              return exceptionFindings.map(finding => ({
                section: finding.section || '',
                team: detail.team || '',
                entity_code: detail.entity_code || '',
                entity_name: detail.entity_name || '',
                ...finding,
              }));
            }
            return [];
          } catch (err) {
            console.error(`Error loading results for ${detail.entity_name}:`, err);
            return [];
          }
        });

        const s3Results = await Promise.all(s3LoadPromises);
        const allFindings = s3Results.flat();

        let totalImportant = 0;
        let totalFyi = 0;
        for (const finding of allFindings) {
          if (finding.severity === 'important') totalImportant++;
          if (finding.severity === 'fyi') totalFyi++;
        }

        allFindings.sort((a, b) => {
          const sectionA = (a.section || '').toLowerCase();
          const sectionB = (b.section || '').toLowerCase();
          if (sectionA < sectionB) return -1;
          if (sectionA > sectionB) return 1;
          return 0;
        });

        const numberedFindings = allFindings.map((finding, index) => ({
          ...finding,
          no: index + 1,
        }));

        const columns = ['no', 'section', 'team', 'entity_code', 'entity_name', 'severity', 'description', 'location', 'current_value', 'expected_value', 'impact', 'action_required'];

        let html = `<h1>Consolidated Exception Report</h1>`;
        html += `<h3>${savedResult.label}</h3>`;
        html += `<table><thead><tr>`;
        html += `<th>Entities Included</th><th>Total Important Findings</th><th>Total FYI Findings</th>`;
        html += `</tr></thead><tbody><tr>`;
        html += `<td>${entityDetails.length}</td><td>${totalImportant}</td><td>${totalFyi}</td>`;
        html += `</tr></tbody></table>`;

        if (numberedFindings.length > 0) {
          html += `<h3>Exception Findings</h3>`;
          html += `<table><thead><tr>`;
          for (const col of columns) {
            html += `<th>${formatHeader(col)}</th>`;
          }
          html += `</tr></thead><tbody>`;
          for (const row of numberedFindings) {
            html += `<tr>`;
            for (const col of columns) {
              const value = row[col] !== null && row[col] !== undefined ? row[col] : '';
              html += `<td>${value}</td>`;
            }
            html += `</tr>`;
          }
          html += `</tbody></table>`;
        } else {
          html += `<p>No exceptions found across the selected entities.</p>`;
        }

        downloadRichPdf(html, { entity_name: savedResult.label || 'Consolidated_Report', current_year: '' }, () => {
          setDownloadLoading(prev => ({ ...prev, [preferenceId]: false }));
        });
      }
    } catch (error) {
      console.error('Error downloading consolidated result:', error);
      setDownloadLoading(prev => ({ ...prev, [preferenceId]: false }));
    }

    setPendingDownloadData(null);
  };

  const viewConsolidatedResult = async (savedResult) => {
    setIsLoadingResults(true);
    setViewingConsolidatedResult(savedResult);

    const requestIds = savedResult.requestIds || [];
    const entityDetails = savedResult.entityDetails || [];
    const resultsPerRequestId = {};

    // Fetch history items for each request_id
    const fetchPromises = requestIds.map(requestId => {
      return new Promise((resolve) => {
        getFirstPassHistoryItem(requestId, 
          function onSuccess(response) {
            const historyItem = response?.data?.getFirstPassHistoryItem;
            if (historyItem) {
              resultsPerRequestId[requestId] = historyItem;
            }
            resolve();
          },
          function onFailure(error) {
            console.error(`Error fetching history item ${requestId}:`, error);
            resolve();
          }
        );
      });
    });

    await Promise.all(fetchPromises);

    // Build selectedResults from entityDetails
    const selectedResults = entityDetails.map(detail => ({
      id: detail.request_id,
      label: detail.entity_name || '',
      entity_code: detail.entity_code || '',
      team: detail.team || '',
    }));

    setViewingConsolidatedResult({
      ...savedResult,
      selectedResults,
      resultsPerRequestId,
    });
    setIsLoadingResults(false);
  };

  return (
    <SpaceBetween direction="vertical" size="m">
      <Container>
        <SpaceBetween direction="vertical" size="m">
          <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
            <Input
              onChange={({ detail }) => setQuickFilterText(detail.value)}
              value={quickFilterText}
              expandToViewport={true}
              placeholder='Type to search saved consolidated results'
            />
            <Box float="right">
              <Button onClick={() => loadSavedConsolidatedResults()} iconName="refresh" variant="icon" />
            </Box>
          </Grid>

          <div style={{ width: '100%', height: '40vh' }}>
            <div className="ag-theme-alpine" style={{ width: '100%', height: '100%', minHeight: '200px' }}>
              <AgGridReact
                ref={gridRef}
                rowData={savedConsolidatedResults}
                defaultColDef={defaultColDef}
                columnDefs={columnDefs}
                rowSelection="single"
                animateRows={true}
                pagination={true}
                paginationPageSize={10}
                paginationPageSizeSelector={[10, 50, 100]}
                loading={isLoading}
              />
            </div>
          </div>
        </SpaceBetween>
      </Container>

      {isLoadingResults && (
        <Box padding="l">
          <StatusIndicator type="loading">Loading consolidated results...</StatusIndicator>
        </Box>
      )}

      {viewingConsolidatedResult && !isLoadingResults && viewingConsolidatedResult.selectedResults && (
        <SpaceBetween direction="vertical" size="m">
          <SpaceBetween direction="vertical" size="xxs">
            <Box variant="h3">
              <img className="ai-button" src={aiInsights} alt="" /> {viewingConsolidatedResult.label}
            </Box>
            <Box>
              <i>AI has limitations and may produce omissions or errors. Please review all important information. Known First Pass PRO limitations are documented on <Link external href="https://w.amazon.com/bin/view/Statcloseprocess/FSQA/FirstPass/">this Wiki page</Link></i>
            </Box>
          </SpaceBetween>
          <FirstpassConsolidatedExceptionsResultComponent
            selectedResults={viewingConsolidatedResult.selectedResults}
            resultsPerRequestId={viewingConsolidatedResult.resultsPerRequestId}
            reportLabel={viewingConsolidatedResult.label}
          />
        </SpaceBetween>
      )}

      <DownloadFormatModal
        visible={showFormatModal}
        onDismiss={() => setShowFormatModal(false)}
        onConfirm={handleDownloadConfirm}
      />
    </SpaceBetween>
  );
};

export default FirstPassConsolidatedHistoryComponent;
